/**
 * CancelledBookingDetailsModal.jsx
 * Module: BookingHistory / Cancelled Bookings
 *
 * The "View" detail modal for the Cancelled Bookings list
 * (/bookingdeletehistory), for BOTH the Air Cargo and Ocean Cargo tabs.
 *
 * Layout is the self-contained `bdm-*` card design (orange header + route
 * strip + underlined tab bar + key/value grids), scoped by its own <style>
 * block so it can't collide with the app's Tailwind/MUI surfaces. The same
 * shell renders either cargo type; only the tab set and the field mapping
 * differ:
 *
 *   Air   — Overview / Flight / Dimensions / Charges   (AWB, flight legs,
 *           package dimensions, chargesBreakDown)
 *   Ocean — Overview / Schedule / Containers / Charges (MBL, vessel legs +
 *           BL rows, container line items, SELL charges in USD/INR)
 *
 * Data comes from the same `getsinglebookingdata` call the previous detail
 * views (NewAircargoDetails / OceanCargoDetails) used, and every field is
 * resolved with the same fallback chains as those files — cancelled bookings
 * carry the same payload shapes, including the snapshot drift those chains
 * work around.
 *
 * The dark-mode block from the source design is intentionally omitted: the app
 * runs on MUI's default (light) theme with no dark toggle, so honouring the
 * OS's `prefers-color-scheme` would render a dark modal over a light page.
 */
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { X, Plane, Ship } from "lucide-react";
import moment from "moment";
import axios from "axios";
import { getsinglebookingdata } from "../../../store/BookingHistory/BookingHistorySlice";
import { formatrates } from "../../../functions/Utils";
import {
  safeNum,
  getFlagByCountryCode,
  parseSegDate,
} from "../../common/BookingDetailUI";
import { OCEAN_CARGO_TYPE_CODE_LABELS } from "../../../utils/constant";
import {
  containerWeightLabel,
  totalContainerWeight,
} from "../../../utils/oceanContainerWeight";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

  .bdm-root {
    --bg: #F0F2F5;
    --surface: #FFFFFF;
    --surface-2: #F9FAFB;
    --border: #E5E8ED;
    --ink: #111827;
    --ink-2: #374151;
    --muted: #6B7280;
    --muted-light: #9CA3AF;
    --orange: #F4821F;
    --orange-light: #FFF3E8;
    --orange-mid: #FDE8D0;
    --cancelled: #DC2626;
    --cancelled-bg: #FEF2F2;
    --confirmed: #16A34A;
    --confirmed-bg: #F0FDF4;
    --pending: #B45309;
    --pending-bg: #FFFBEB;
    --shadow: 0 8px 40px rgba(0,0,0,0.13), 0 2px 8px rgba(0,0,0,0.06);
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
  }

  /* Overlay — the design ships the card only, so the dimmed backdrop that
     hosts it lives here. The generous top/bottom padding is what keeps the
     card clear of the viewport edges. */
  .bdm-overlay {
    position: fixed;
    inset: 0;
    z-index: 1000;
    background: rgba(17,24,39,0.55);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 32px 16px;
    overflow-y: auto;
  }

  /* Content still scrolls (wheel / touch / keyboard) when a booking has more
     rows than fit — the bar itself is hidden so it can't cut into the card's
     rounded edge next to the tab bar. */
  .bdm-overlay,
  .bdm-tab-content,
  .bdm-tabs-bar { scrollbar-width: none; -ms-overflow-style: none; }

  .bdm-overlay::-webkit-scrollbar,
  .bdm-tab-content::-webkit-scrollbar,
  .bdm-tabs-bar::-webkit-scrollbar { width: 0; height: 0; display: none; }

  .bdm-modal {
    background: var(--surface);
    border-radius: 16px;
    width: 100%;
    max-width: 640px;
    box-shadow: var(--shadow);
    overflow: hidden;
    display: flex;
    flex-direction: column;
    /* Matches the overlay's 32px top/bottom padding, so the card always sits
       inside that margin instead of running to the viewport edge. */
    max-height: calc(100vh - 64px);
  }

  /* Header */
  .bdm-header {
    background: var(--orange);
    padding: 18px 22px 16px;
    display: flex;
    align-items: flex-start;
    gap: 14px;
    flex-shrink: 0;
  }

  .bdm-airline-logo {
    width: 44px;
    height: 44px;
    background: rgba(255,255,255,0.18);
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    border: 1.5px solid rgba(255,255,255,0.3);
    overflow: hidden;
  }

  .bdm-airline-logo img { width: 100%; height: 100%; object-fit: contain; }

  .bdm-header-meta { flex: 1; min-width: 0; }

  .bdm-header-top {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }

  .bdm-header-title {
    font-size: 17px;
    font-weight: 700;
    color: #fff;
    letter-spacing: -0.01em;
  }

  .bdm-status-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    padding: 3px 9px;
    border-radius: 20px;
    background: rgba(255,255,255,0.22);
    color: #fff;
    border: 1px solid rgba(255,255,255,0.3);
  }

  .bdm-status-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #fff;
    flex-shrink: 0;
  }

  .bdm-awb-line {
    margin-top: 4px;
    font-size: 12.5px;
    color: rgba(255,255,255,0.72);
    font-family: 'Courier New', monospace;
    letter-spacing: 0.04em;
  }

  .bdm-booked-date {
    font-size: 12px;
    color: rgba(255,255,255,0.65);
    margin-top: 2px;
  }

  .bdm-close-btn {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: 1.5px solid rgba(255,255,255,0.3);
    background: rgba(255,255,255,0.12);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    flex-shrink: 0;
    transition: background 0.15s;
  }

  .bdm-close-btn:hover { background: rgba(255,255,255,0.22); }

  /* Route card */
  .bdm-route-card {
    background: var(--orange-light);
    border-bottom: 1px solid var(--border);
    padding: 16px 22px;
    display: flex;
    align-items: center;
    flex-shrink: 0;
  }

  .bdm-route-point { display: flex; flex-direction: column; gap: 2px; min-width: 80px; }
  .bdm-route-point.right { align-items: flex-end; }

  .bdm-iata {
    font-size: 26px;
    font-weight: 800;
    color: var(--ink);
    letter-spacing: -0.02em;
    line-height: 1;
  }

  .bdm-city { font-size: 12px; color: var(--muted); font-weight: 500; }
  .bdm-country { font-size: 11px; color: var(--muted-light); font-weight: 400; text-transform: uppercase; letter-spacing: 0.04em; }

  .bdm-connector {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    padding: 0 12px;
    min-width: 0;
  }

  .bdm-connector-line {
    display: flex;
    align-items: center;
    width: 100%;
    gap: 4px;
  }

  .bdm-line-seg { flex: 1; height: 1.5px; background: var(--orange); opacity: 0.4; }
  .bdm-plane { color: var(--orange); flex-shrink: 0; }
  .bdm-carrier-label { font-size: 11px; font-weight: 600; color: var(--orange); letter-spacing: 0.03em; text-align: center; }
  .bdm-dep-info { font-size: 11px; color: var(--muted); text-align: center; }

  /* Tabs */
  .bdm-tabs-bar {
    display: flex;
    border-bottom: 1px solid var(--border);
    padding: 0 22px;
    flex-shrink: 0;
    overflow-x: auto;
  }

  .bdm-tab-btn {
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 13px;
    font-weight: 500;
    color: var(--muted);
    background: none;
    border: none;
    cursor: pointer;
    padding: 12px 16px 11px;
    position: relative;
    transition: color 0.15s;
    white-space: nowrap;
  }

  .bdm-tab-btn:hover { color: var(--ink-2); }

  .bdm-tab-btn.active {
    color: var(--orange);
    font-weight: 600;
  }

  .bdm-tab-btn.active::after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 16px;
    right: 16px;
    height: 2px;
    background: var(--orange);
    border-radius: 2px 2px 0 0;
  }

  /* Content */
  .bdm-tab-content { padding: 20px 22px 24px; overflow-y: auto; }

  .bdm-section { margin-bottom: 20px; }
  .bdm-section:last-child { margin-bottom: 0; }

  .bdm-section-label {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--muted);
    margin-bottom: 10px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .bdm-section-label::after { content: ''; flex: 1; height: 1px; background: var(--border); }

  .bdm-kv-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1px;
    background: var(--border);
    border-radius: 10px;
    overflow: hidden;
    border: 1px solid var(--border);
  }

  .bdm-kv-item { background: var(--surface-2); padding: 11px 14px; }
  .bdm-kv-item.full { grid-column: 1 / -1; }
  .bdm-kv-key { font-size: 11px; color: var(--muted); font-weight: 500; margin-bottom: 3px; text-transform: uppercase; letter-spacing: 0.04em; }
  .bdm-kv-val { font-size: 13.5px; color: var(--ink); font-weight: 600; word-break: break-word; }
  .bdm-kv-val.mono { font-family: 'Courier New', monospace; font-size: 13px; letter-spacing: 0.02em; }
  .bdm-kv-sub { font-size: 11px; color: var(--muted); font-weight: 500; margin-top: 2px; }

  .bdm-status-pill {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 12px;
    font-weight: 600;
    padding: 3px 9px;
    border-radius: 20px;
  }

  .bdm-status-pill.cancelled { background: var(--cancelled-bg); color: var(--cancelled); }
  .bdm-status-pill.confirmed { background: var(--confirmed-bg); color: var(--confirmed); }
  .bdm-status-pill.pending { background: var(--pending-bg); color: var(--pending); }

  /* Charges */
  .bdm-charges-wrap { border: 1px solid var(--border); border-radius: 10px; overflow: hidden; }

  .bdm-charges-table { width: 100%; border-collapse: collapse; font-size: 13px; }
  .bdm-charges-table th {
    text-align: left; font-size: 11px; font-weight: 700; text-transform: uppercase;
    letter-spacing: 0.06em; color: var(--muted); padding: 8px 14px;
    background: var(--surface-2); border-bottom: 1px solid var(--border);
  }
  .bdm-charges-table th:last-child { text-align: right; }
  .bdm-charges-table td { padding: 10px 14px; color: var(--ink-2); border-bottom: 1px solid var(--border); font-weight: 500; }
  .bdm-charges-table td:last-child { text-align: right; font-variant-numeric: tabular-nums; font-weight: 600; color: var(--ink); }
  .bdm-charges-table tr:last-child td { border-bottom: none; }

  /* Group header row inside the charge table (Freight / Origin / Destination) */
  .bdm-charges-table tr.bdm-charge-group td {
    background: var(--orange-light);
    color: var(--ink);
    font-weight: 700;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  .bdm-charges-table tr.bdm-charge-sub td:first-child { padding-left: 26px; color: var(--muted); }

  .bdm-charges-total {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 14px;
    background: var(--orange-mid);
    border-top: 1px solid var(--border);
    gap: 10px;
  }

  .bdm-charges-total-label { font-size: 13px; font-weight: 700; color: var(--ink); text-transform: uppercase; letter-spacing: 0.04em; }
  .bdm-charges-total-val { font-size: 16px; font-weight: 800; color: var(--orange); font-variant-numeric: tabular-nums; text-align: right; }

  .bdm-charges-note {
    display: flex; flex-wrap: wrap; gap: 6px 18px;
    margin-top: 10px; padding: 10px 14px;
    background: var(--orange-light); border: 1px solid var(--orange-mid);
    border-radius: 10px; font-size: 12px; color: var(--ink-2);
  }
  .bdm-charges-note b { color: var(--ink); font-weight: 700; }

  /* Dimensions / generic data tables */
  .bdm-dim-wrap { border: 1px solid var(--border); border-radius: 10px; overflow: hidden; overflow-x: auto; }
  .bdm-dim-table { width: 100%; border-collapse: collapse; font-size: 13px; }
  .bdm-dim-table th {
    font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em;
    color: var(--muted); padding: 8px 14px; text-align: left;
    background: var(--surface-2); border-bottom: 1px solid var(--border);
    white-space: nowrap;
  }
  .bdm-dim-table td { padding: 10px 14px; color: var(--ink-2); border-bottom: 1px solid var(--border); font-variant-numeric: tabular-nums; font-weight: 500; white-space: nowrap; }
  .bdm-dim-table tr:last-child td { border-bottom: none; }

  .bdm-empty { padding: 40px 0; text-align: center; font-size: 13px; color: var(--muted); }

  /* Loading skeleton */
  .bdm-skel { height: 14px; border-radius: 6px; background: var(--border); margin-bottom: 10px; animation: bdmPulse 1.2s ease-in-out infinite; }
  .bdm-skel.lg { height: 90px; }
  @keyframes bdmPulse { 0%,100% { opacity: 1; } 50% { opacity: 0.45; } }
`;

const AIR_TABS = [
  { key: "overview", label: "Overview" },
  { key: "flight", label: "Flight" },
  { key: "dimensions", label: "Dimensions" },
  { key: "charges", label: "Charges" },
];

const OCEAN_TABS = [
  { key: "overview", label: "Overview" },
  { key: "schedule", label: "Schedule" },
  { key: "containers", label: "Containers" },
  { key: "charges", label: "Charges" },
];

/** Which pill colour a booking/payment status maps to. */
const statusTone = (status) => {
  const s = (status || "").toLowerCase();
  if (s.includes("cancel") || s.includes("reject")) return "cancelled";
  if (s.includes("confirmed") || s.includes("completed")) return "confirmed";
  return "pending";
};

const dash = (value) =>
  value == null || value === "" || value === "undefined" || value === "null"
    ? "N/A"
    : value;

/** "$ 1,234.00" — ocean charges are quoted in USD. */
const usd = (value) =>
  `$ ${Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

/**
 * Air waybill numbers are stored without the airline prefix when they're the
 * short 8-digit form; prepend it so the modal shows the full "157-43662986"
 * shape. Same rule NewAircargoDetails applied.
 */
const formatAirwayBillNumber = (num, airlinePrefix) => {
  if (!num) return "";
  const str = String(num).trim();
  if (str === "undefined" || str === "null" || str.length === 0) return "";
  if (!airlinePrefix) return str;
  if (str.length > 8) return str;
  return `${airlinePrefix}-${str}`;
};

// chargesBreakDown items arrive in two shapes from the backend:
//   old: { chargeType, chargeDescription, chargeAmount }
//   new: { category, name, Totalamount, ratePerkilo }
// Normalize both, and coerce the amount so a string amount doesn't turn the
// subtotal reduce() into string concatenation.
const getChargeDescription = (c) => c?.chargeDescription || c?.name || "";
const getChargeAmount = (c) => {
  const n = Number(c?.chargeAmount ?? c?.Totalamount ?? c?.totalAmount ?? 0);
  return Number.isFinite(n) ? n : 0;
};

const groupBy = (list, keyOf) =>
  list.reduce((acc, item) => {
    const k = keyOf(item);
    if (!acc[k]) acc[k] = [];
    acc[k].push(item);
    return acc;
  }, {});

/**
 * @param {Object} props
 * @param {string} props.queryid - booking query id of the cancelled row
 * @param {boolean} props.isOcean - render the ocean field set instead of air
 * @param {string} [props.cancelledAt] - the row's `deletedAt`; the single-booking
 *   endpoint doesn't return it, so the list passes it through for the
 *   "Cancelled On" cell.
 * @param {Function} props.onClose - closes the modal
 */
const CancelledBookingDetailsModal = ({
  queryid,
  isOcean = false,
  cancelledAt,
  onClose,
}) => {
  const dispatch = useDispatch();
  const [nodeData, setNodeData] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  // Air commodity description, resolved through the commodity master (the
  // booking payload only carries the id) — same two-step lookup
  // NewAircargoDetails used.
  const [commodityDesc, setCommodityDesc] = useState("");

  const tabs = isOcean ? OCEAN_TABS : AIR_TABS;

  useEffect(() => {
    if (!queryid) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    dispatch(getsinglebookingdata({ queryId: queryid }))
      .then((res) => {
        if (cancelled) return;
        setNodeData(res?.payload?.data?.data || {});
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [queryid, dispatch]);

  const bookingDetail = nodeData?.bookeditem || {};
  const searchquery = nodeData?.search_query_data || nodeData?.searchquery || {};

  // ── Air commodity description via the commodity master ───────────────────
  const commodityId = searchquery?.goodsDetails?.commodityId;
  useEffect(() => {
    if (isOcean || !commodityId) return;
    let cancelledLookup = false;
    const token = localStorage.getItem("tokenairline");
    axios
      .get(`https://cdutils.sgate.net/api/v1/new/commoditydetails/${commodityId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (cancelledLookup) return;
        setCommodityDesc(res?.data?.data?.[0]?.commodity_description || "");
      })
      .catch(() => {});
    return () => {
      cancelledLookup = true;
    };
  }, [commodityId, isOcean]);

  const bookingStatus = nodeData?.booking_status || "Booking Cancelled";

  // ═══════════════════════ AIR field mapping ═══════════════════════════════
  const itineraries = bookingDetail?.flightItineraries || [];
  const airPrefix = itineraries?.[0]?.airlinePrefix;
  const awb =
    formatAirwayBillNumber(bookingDetail?.airWaybillNumber, airPrefix) || "N/A";

  const airCarrierName = (() => {
    const code = itineraries?.[0]?.carrierCode;
    if (code === "QR") return "Qatar Airways";
    return bookingDetail?.airlineName || code || "N/A";
  })();

  /**
   * "28 Aug 2026 08:45" for a flight segment. Both fields vary by rate source
   * — the date is "DD-MMM-YYYY" or ISO, the time is ISO ("…T08:45") or a
   * "date time" pair — so each is parsed rather than string-split.
   */
  const segmentWhen = (date, time) => {
    const d = date ? parseSegDate(date) : null;
    const t = time ? parseSegDate(time) : null;
    return [
      d?.isValid() ? d.format("DD MMM YYYY") : "N/A",
      t?.isValid() ? t.format("HH:mm") : "",
    ]
      .join(" ")
      .trim();
  };

  /** "QR 551" — skips the code when the number already carries it. */
  const segmentFlightNo = (segment) => {
    const code = segment?.rates_from === "FS" ? "" : segment?.carrierCode || "";
    const num = segment?.carrierNumber || "";
    const alreadyPrefixed =
      code && num.slice(0, 2).toLowerCase() === code.slice(0, 2).toLowerCase();
    return `${alreadyPrefixed ? "" : code + " "}${num}`.trim() || "N/A";
  };

  // Volume / chargeable weight — recomputed with the search form's own
  // formula (calcRowTotals in NewsearchForm) rather than trusting the stored
  // rateDetails, which older bookings saved with the wrong unit.
  const packages = searchquery?.packagingDetails?.packagingType?.packages || [];
  const weightMode = packages[0]?.weightunit === "KG" ? 2 : 1;
  const weightUnit =
    searchquery?.packagingDetails?.shipmentTotal?.weightunit ||
    bookingDetail?.rateDetails?.chargeableWeightUnit ||
    "KG";
  const volUnit = searchquery?.packagingDetails?.shipmentTotal?.unit || "CBM";
  // CFT means the booking was entered in inches — use inch math even when
  // pkg.unit says 'CM' (old bookings can have the wrong unit saved).
  const isInchMode = volUnit === "CFT";

  const rowTotals = packages.map((pkg) => {
    const units = parseFloat(pkg.units) || 0;
    const l = parseFloat(pkg.length) || 0;
    const w = parseFloat(pkg.width) || 0;
    const h = parseFloat(pkg.height) || 0;
    const wt = parseFloat(pkg.weight) || 0;
    const totalWt = weightMode === 2 ? wt : units * wt;
    let volWeight;
    if (pkg.unit === "IN" || isInchMode) {
      volWeight =
        (units * Math.round(l * 2.54) * Math.round(w * 2.54) * Math.round(h * 2.54)) /
        6000;
    } else {
      volWeight = (units * l * w * h) / 6000;
    }
    return {
      totalsubweight: isNaN(totalWt) ? 0 : parseFloat(totalWt.toFixed(2)),
      volWeight: isNaN(volWeight) ? 0 : parseFloat(volWeight.toFixed(2)),
    };
  });
  const totalGW = parseFloat(
    rowTotals.reduce((s, r) => s + r.totalsubweight, 0).toFixed(2)
  );
  const totalVW = parseFloat(rowTotals.reduce((s, r) => s + r.volWeight, 0).toFixed(2));
  const chargeWt = Math.ceil(Math.max(totalGW, totalVW));
  const chargeableWtDisplay = packages.length ? `${chargeWt} ${weightUnit}` : null;

  const airCharges = bookingDetail?.rateDetails?.chargesBreakDown || [];

  // ═══════════════════════ OCEAN field mapping ═════════════════════════════
  const offer = nodeData?.ocean_offer || {};
  const schedule = nodeData?.ocean_schedule || {};
  const mblNumber = nodeData?.mblNumber || nodeData?.airWaybillNumber;
  const oceanOrigin =
    schedule?.fromLocation?.unLocCode ||
    offer?.productOffer?.originPort ||
    searchquery?.originPort?.originPort ||
    "N/A";
  const oceanDestination =
    schedule?.toLocation?.unLocCode ||
    offer?.productOffer?.destinationPort ||
    searchquery?.destinationPort?.destinationPort ||
    "N/A";
  const oceanCarrier = offer?.productOffer?.carrierName || "N/A";
  const rawCommodity =
    nodeData?.commodity || searchquery?.goodsDetails?.commodity_description;
  const oceanCommodity =
    OCEAN_CARGO_TYPE_CODE_LABELS[rawCommodity] || rawCommodity || "General Cargo";
  const blDetails =
    [nodeData?.blDetails, nodeData?.queryData?.blDetails, searchquery?.blDetails].find(
      (list) => Array.isArray(list) && list.length > 0
    ) || [];
  const scheduleDetails = schedule?.scheduleDetails || [];
  // A live-search booking nests these as containerDetails.containerDetails; a
  // manual-quote booking stores the raw admin Query shape (a flat array).
  const containerLineItems = Array.isArray(searchquery?.containerDetails)
    ? searchquery.containerDetails
    : searchquery?.containerDetails?.containerDetails || [];
  // kgs is the line total (legacy rows without weightType are per container).
  const totalKgs = totalContainerWeight(containerLineItems);

  const sellCharges = (offer?.productPrice?.charges || []).filter(
    (c) => c.rateType === "SELL"
  );
  const oceanChargeGroups = groupBy(sellCharges, (c) => c.rateTypeCode || "OTHER");
  const GROUP_ORDER = ["ORIGIN", "FREIGHT", "DESTINATION"];
  const GROUP_LABEL = {
    ORIGIN: "Origin Charges",
    FREIGHT: "Freight Charges",
    DESTINATION: "Destination Charges",
  };
  const oceanGroupKeys = [
    ...GROUP_ORDER.filter((k) => oceanChargeGroups[k]?.length),
    ...Object.keys(oceanChargeGroups).filter((k) => !GROUP_ORDER.includes(k)),
  ];
  const oceanGroupTotal = (k) =>
    oceanChargeGroups[k].reduce(
      (s, c) => s + (safeNum(c.amountUsd) ?? safeNum(c.amount) ?? 0),
      0
    );
  // `productPrice.charges` is the pre-markup rate card persisted at booking
  // time; the markup-inclusive grand total lives on the booking as usd_amount.
  const rawChargesTotal =
    oceanGroupKeys.length > 0
      ? oceanGroupKeys.reduce((s, k) => s + oceanGroupTotal(k), 0)
      : safeNum(offer?.productPrice?.totalUSDAmount?.SELL) ?? 0;
  const totalUsd = safeNum(nodeData?.usd_amount) ?? rawChargesTotal;
  const totalInr = safeNum(nodeData?.total_amount);
  const gstUsd =
    safeNum(nodeData?.bookeditem?.gst_usd_amount ?? nodeData?.gst_usd_amount) ?? 0;
  const taxableUsd =
    safeNum(nodeData?.bookeditem?.taxable_usd_amount ?? nodeData?.taxable_usd_amount) ??
    0;
  const gstInr = safeNum(
    nodeData?.bookeditem?.gst_inr_amount ?? nodeData?.gst_inr_amount
  );
  const taxableInr = safeNum(
    nodeData?.bookeditem?.taxable_inr_amount ?? nodeData?.taxable_inr_amount
  );

  // ── Shared header / route strip values ───────────────────────────────────
  const trackingLabel = isOcean ? "MBL" : "AWB";
  const trackingNumber = isOcean ? mblNumber || "N/A" : awb;
  // `booking_confirm_date` sits at the top level of the booking record and is
  // the date the booking was placed — no fallback, so a record without it
  // reads "N/A" rather than showing some other date as the booked-on date.
  const bookedOnRaw = nodeData?.booking_confirm_date;
  const bookedOn = bookedOnRaw ? moment(bookedOnRaw).format("DD MMM YYYY") : "N/A";

  // cargoreadydate is "DD/MM/YYYY" (not ISO) unlike the ocean schedule
  // fallbacks, so each source is parsed with its own known format.
  const oceanDepartureMoment = searchquery?.goodsDetails?.cargoreadydate
    ? moment(searchquery.goodsDetails.cargoreadydate, "DD/MM/YYYY")
    : schedule?.fromLocation?.departure
    ? moment(schedule.fromLocation.departure)
    : schedule?.sailingDate
    ? moment(schedule.sailingDate)
    : null;
  // The air departure date lives on the first flight segment; only some
  // bookings also copy it up onto bookeditem. Segment dates arrive as
  // "28-Aug-2026" (or full ISO, depending on the rate source), so parseSegDate
  // is used instead of letting moment guess and print "Invalid date".
  const airDepartureRaw =
    bookingDetail?.scheduledDepartureDate ||
    itineraries?.[0]?.scheduledDepartureDate;
  const departureDisplay = isOcean
    ? oceanDepartureMoment
      ? oceanDepartureMoment.format("DD MMM YYYY")
      : "N/A"
    : airDepartureRaw
    ? parseSegDate(airDepartureRaw).format("DD MMM YYYY")
    : "N/A";

  const route = isOcean
    ? {
        origin: {
          code: oceanOrigin,
          city: schedule?.fromLocation?.portName || "",
          country: `${getFlagByCountryCode(
            oceanOrigin !== "N/A" ? oceanOrigin : ""
          )} ${oceanOrigin !== "N/A" ? oceanOrigin.slice(0, 2) : ""}`,
        },
        destination: {
          code: oceanDestination,
          city: schedule?.toLocation?.portName || "",
          country: `${getFlagByCountryCode(
            oceanDestination !== "N/A" ? oceanDestination : ""
          )} ${oceanDestination !== "N/A" ? oceanDestination.slice(0, 2) : ""}`,
        },
        carrier: oceanCarrier,
      }
    : {
        origin: {
          code: bookingDetail?.origin || "N/A",
          city: searchquery?.originPort?.origincityName || "",
          country: searchquery?.originPort?.origincountryName || "",
        },
        destination: {
          code: bookingDetail?.destination || "N/A",
          city: searchquery?.destinationPort?.destinationcityName || "",
          country: searchquery?.destinationPort?.destinationcountryName || "",
        },
        carrier: airCarrierName,
      };

  const ModeIcon = isOcean ? Ship : Plane;

  /** One key/value cell of a `bdm-kv-grid`. */
  const KV = ({ label, value, full, mono, sub }) => (
    <div className={`bdm-kv-item${full ? " full" : ""}`}>
      <div className="bdm-kv-key">{label}</div>
      <div className={`bdm-kv-val${mono ? " mono" : ""}`}>{dash(value)}</div>
      {sub ? <div className="bdm-kv-sub">{sub}</div> : null}
    </div>
  );

  return (
    <>
      <style>{styles}</style>

      <div
        className="bdm-root bdm-overlay"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose?.();
        }}
      >
        <div className="bdm-modal">
          {/* ── Header ───────────────────────────────────────────────── */}
          <div className="bdm-header">
            <div className="bdm-airline-logo">
              {!isOcean && bookingDetail?.ailinelogoUrl ? (
                <img
                  src={bookingDetail.ailinelogoUrl}
                  alt="Carrier"
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              ) : (
                <ModeIcon size={22} color="rgba(255,255,255,0.9)" />
              )}
            </div>

            <div className="bdm-header-meta">
              <div className="bdm-header-top">
                <span className="bdm-header-title">Booking Details</span>
                <span className="bdm-status-badge">
                  <span className="bdm-status-dot" />
                  {bookingStatus}
                </span>
              </div>
              <div className="bdm-awb-line">
                {trackingLabel}: {trackingNumber}
              </div>
              <div className="bdm-booked-date">Booked on {bookedOn}</div>
            </div>

            <button className="bdm-close-btn" onClick={onClose} aria-label="Close">
              <X size={14} />
            </button>
          </div>

          {loading ? (
            <div className="bdm-tab-content">
              <div className="bdm-skel" style={{ width: "60%" }} />
              <div className="bdm-skel" style={{ width: "40%" }} />
              <div className="bdm-skel lg" />
              <div className="bdm-skel lg" />
            </div>
          ) : (
            <>
              {/* ── Route card ─────────────────────────────────────── */}
              <div className="bdm-route-card">
                <div className="bdm-route-point">
                  <div className="bdm-iata">{route.origin.code}</div>
                  <div className="bdm-city">{route.origin.city}</div>
                  <div className="bdm-country">{route.origin.country}</div>
                </div>

                <div className="bdm-connector">
                  <div className="bdm-connector-line">
                    <div className="bdm-line-seg" />
                    <ModeIcon className="bdm-plane" size={18} />
                    <div className="bdm-line-seg" />
                  </div>
                  <div className="bdm-carrier-label">{route.carrier}</div>
                  <div className="bdm-dep-info">Departure: {departureDisplay}</div>
                </div>

                <div className="bdm-route-point right">
                  <div className="bdm-iata">{route.destination.code}</div>
                  <div className="bdm-city">{route.destination.city}</div>
                  <div className="bdm-country">{route.destination.country}</div>
                </div>
              </div>

              {/* ── Tabs ───────────────────────────────────────────── */}
              <div className="bdm-tabs-bar">
                {tabs.map((tab) => (
                  <button
                    key={tab.key}
                    className={`bdm-tab-btn${activeTab === tab.key ? " active" : ""}`}
                    onClick={() => setActiveTab(tab.key)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* ── Tab content ────────────────────────────────────── */}
              <div className="bdm-tab-content">
                {/* ───────────────── Overview ───────────────── */}
                {activeTab === "overview" && (
                  <>
                    {/* Booking / Shipment — exactly the keys the design shows:
                        Status, Booked On, tracking number + commodity, pieces,
                        gross weight, chargeable weight, special handling. Each
                        value falls back to whichever path the booking payload
                        actually carries (packagingDetails is absent on
                        bookings taken straight off a rate, so the flight
                        segment's own pcs/weight stand in). */}
                    <div className="bdm-section">
                      <div className="bdm-section-label">Booking</div>
                      <div className="bdm-kv-grid">
                        <div className="bdm-kv-item">
                          <div className="bdm-kv-key">Status</div>
                          <div className="bdm-kv-val">
                            <span
                              className={`bdm-status-pill ${statusTone(bookingStatus)}`}
                            >
                              <svg width="6" height="6" viewBox="0 0 6 6">
                                <circle cx="3" cy="3" r="3" fill="currentColor" />
                              </svg>
                              {bookingStatus}
                            </span>
                          </div>
                        </div>
                        <KV label="Booked On" value={bookedOn} />
                        <KV
                          label={`${trackingLabel} Number`}
                          value={trackingNumber}
                          mono
                          full
                        />
                      </div>
                    </div>

                    <div className="bdm-section">
                      <div className="bdm-section-label">Shipment</div>
                      {isOcean ? (
                        <div className="bdm-kv-grid">
                          <KV label="Commodity Type" value={oceanCommodity} />
                          <KV
                            label="Cargo Type"
                            value={offer?.productPrice?.cargoType}
                          />
                          <KV
                            label="Container Load"
                            value={searchquery?.containerLoad}
                          />
                          <KV
                            label="Total Weight"
                            value={totalKgs > 0 ? `${totalKgs} KG` : "N/A"}
                          />
                        </div>
                      ) : (
                        <div className="bdm-kv-grid">
                          <KV
                            label="Commodity Type"
                            value={
                              commodityDesc ||
                              bookingDetail?.goodsDetails?.commodity_description ||
                              searchquery?.goodsDetails?.commodity_description ||
                              bookingDetail?.productCommodityDetails?.natureOfGoods ||
                              "General Cargo"
                            }
                          />
                          <KV
                            label="Total Pieces"
                            value={
                              searchquery?.packagingDetails?.packagingType
                                ?.totalunits ??
                              itineraries?.[0]?.pcs ??
                              "N/A"
                            }
                          />
                          <KV
                            label="Gross Weight"
                            value={
                              searchquery?.packagingDetails?.shipmentTotal
                                ?.totalweight != null
                                ? `${searchquery.packagingDetails.shipmentTotal.totalweight} ${weightUnit}`
                                : itineraries?.[0]?.weight != null
                                ? `${itineraries[0].weight} KG`
                                : "N/A"
                            }
                          />
                          <KV
                            label="Chargeable Weight"
                            value={
                              chargeableWtDisplay ||
                              (bookingDetail?.rateDetails?.chargeableWeight != null
                                ? `${bookingDetail.rateDetails.chargeableWeight} ${
                                    // rateDetails spells kilograms as the IATA
                                    // code "K" — show it as "KG".
                                    (bookingDetail?.rateDetails
                                      ?.chargeableWeightUnit || "KG") === "K"
                                      ? "KG"
                                      : bookingDetail.rateDetails
                                          .chargeableWeightUnit
                                  }`
                                : "N/A")
                            }
                          />
                          <KV
                            label="Special Handling"
                            value={
                              [
                                ...new Set([
                                  ...(bookingDetail?.productCommodityDetails
                                    ?.specialHandlingCodes || []),
                                  ...(searchquery?.goodsDetails
                                    ?.specialHandlingCodes || []),
                                ]),
                              ]
                                .filter(Boolean)
                                .join(", ") || "None"
                            }
                            full
                          />
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* ───────────────── Air: Flight ───────────────── */}
                {activeTab === "flight" && !isOcean && (
                  <>
                    {itineraries.length === 0 ? (
                      <div className="bdm-empty">
                        No flight itinerary available for this booking.
                      </div>
                    ) : (
                      // Flight No. / Carrier / Departure / Arrival / Origin /
                      // Destination, one grid per leg — a direct booking gets
                      // the single "Flight Info" card, and a connecting one
                      // repeats it per segment with its own stations.
                      itineraries.map((segment, index) => (
                        <div className="bdm-section" key={index}>
                          <div className="bdm-section-label">
                            {itineraries.length > 1
                              ? `Flight Info — Segment ${index + 1}`
                              : "Flight Info"}
                          </div>
                          <div className="bdm-kv-grid">
                            <KV
                              label="Flight No."
                              value={segmentFlightNo(segment)}
                              mono
                            />
                            <KV label="Carrier" value={route.carrier} />
                            {/* Date only — the segment's clock time is left
                                out to keep these cells single-line. */}
                            <KV
                              label="Departure"
                              value={segmentWhen(segment?.scheduledDepartureDate)}
                            />
                            <KV
                              label="Arrival"
                              value={segmentWhen(segment?.scheduledArrivalDate)}
                            />
                            <KV
                              label="Origin"
                              value={
                                segment?.segmentOfDeparture
                                  ? `${segment.segmentOfDeparture}${
                                      index === 0 && route.origin.city
                                        ? ` — ${route.origin.city}`
                                        : ""
                                    }`
                                  : "N/A"
                              }
                            />
                            <KV
                              label="Destination"
                              value={
                                segment?.segmentOfArrival
                                  ? `${segment.segmentOfArrival}${
                                      index === itineraries.length - 1 &&
                                      route.destination.city
                                        ? ` — ${route.destination.city}`
                                        : ""
                                    }`
                                  : "N/A"
                              }
                            />
                          </div>
                        </div>
                      ))
                    )}
                  </>
                )}

                {/* ───────────────── Air: Dimensions ───────────────── */}
                {activeTab === "dimensions" && !isOcean && (
                  <div className="bdm-section">
                    <div className="bdm-section-label">Package Dimensions</div>
                    {packages.length === 0 ? (
                      <div className="bdm-empty">
                        No package dimensions available for this booking.
                      </div>
                    ) : (
                      // Pieces / L / W / H / Wt only. The dimension and weight
                      // units are read off the packages themselves and shown in
                      // the headers, since a booking can be entered in IN/LB
                      // rather than the CM/KG the design assumes.
                      <div className="bdm-dim-wrap">
                        <table className="bdm-dim-table">
                          <thead>
                            <tr>
                              <th>Pieces</th>
                              <th>L ({packages[0]?.unit || "CM"})</th>
                              <th>W ({packages[0]?.unit || "CM"})</th>
                              <th>H ({packages[0]?.unit || "CM"})</th>
                              <th>Wt ({packages[0]?.weightunit || "KG"})</th>
                            </tr>
                          </thead>
                          <tbody>
                            {packages.map((dim, i) => (
                              <tr key={i}>
                                <td>{dash(dim?.units)}</td>
                                <td>{dash(dim?.length)}</td>
                                <td>{dash(dim?.width)}</td>
                                <td>{dash(dim?.height)}</td>
                                <td>{dash(dim?.weight)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {/* ───────────────── Ocean: Schedule ───────────────── */}
                {activeTab === "schedule" && isOcean && (
                  <>
                    {scheduleDetails.length === 0 ? (
                      <div className="bdm-section">
                        <div className="bdm-section-label">Schedule Details</div>
                        <div className="bdm-empty">
                          No sailing schedule available for this booking.
                        </div>
                      </div>
                    ) : (
                      // Ocean mirror of the Air "Flight Info" cards above: one
                      // kv-grid per leg instead of a five-column table, which
                      // scrolled sideways on a narrow modal and buried the
                      // vessel behind a "Leg" cell. Field order matches air
                      // one-for-one — identifier, operator, dates, stations —
                      // so the two tabs read the same way.
                      scheduleDetails.map((leg, i) => (
                        <div className="bdm-section" key={i}>
                          <div className="bdm-section-label">
                            {scheduleDetails.length > 1
                              ? `Schedule Details — Leg ${i + 1}`
                              : "Schedule Details"}
                          </div>
                          <div className="bdm-kv-grid">
                            <KV
                              label="Voyage No."
                              value={leg?.transport?.voyageNumber}
                              mono
                            />
                            <KV label="Vessel" value={leg?.transport?.vessel?.name} />
                            {/* Date only, no clock time — same as the air
                                segments, to keep every cell single-line. */}
                            <KV
                              label="Departure"
                              value={
                                leg?.fromLocation?.departure
                                  ? moment(leg.fromLocation.departure).format(
                                      "DD MMM YYYY"
                                    )
                                  : "N/A"
                              }
                            />
                            <KV
                              label="Arrival"
                              value={
                                leg?.toLocation?.arrival
                                  ? moment(leg.toLocation.arrival).format(
                                      "DD MMM YYYY"
                                    )
                                  : "N/A"
                              }
                            />
                            {/* "INMUN — MUNDRA", the air cards' "DEL — Delhi"
                                shape. The names come from the same place the
                                header banner reads them (route.origin.city /
                                route.destination.city, i.e. ocean_schedule's
                                own fromLocation/toLocation) rather than the
                                per-leg portName, which carriers fill
                                inconsistently — Hapag-Lloyd returns it on the
                                arrival side and leaves departure bare, which is
                                why Origin showed a naked "INMUN".
                                Only named when the leg's code matches the
                                voyage end, so an intermediate transhipment port
                                on a multi-leg sailing isn't labelled with the
                                voyage's origin/destination name. */}
                            <KV
                              label="Origin"
                              value={
                                leg?.fromLocation?.unLocCode
                                  ? `${leg.fromLocation.unLocCode}${
                                      leg.fromLocation.unLocCode ===
                                        route.origin.code && route.origin.city
                                        ? ` — ${route.origin.city}`
                                        : ""
                                    }`
                                  : "N/A"
                              }
                            />
                            <KV
                              label="Destination"
                              value={
                                leg?.toLocation?.unLocCode
                                  ? `${leg.toLocation.unLocCode}${
                                      leg.toLocation.unLocCode ===
                                        route.destination.code &&
                                      route.destination.city
                                        ? ` — ${route.destination.city}`
                                        : ""
                                    }`
                                  : "N/A"
                              }
                            />
                          </div>
                        </div>
                      ))
                    )}

                    {blDetails.length > 0 && (
                      <div className="bdm-section">
                        <div className="bdm-section-label">BL Details</div>
                        <div className="bdm-dim-wrap">
                          <table className="bdm-dim-table">
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>Confirmation Number</th>
                                <th>Container Number</th>
                                <th>BL Number</th>
                              </tr>
                            </thead>
                            <tbody>
                              {blDetails.map((row, i) => (
                                <tr key={i}>
                                  <td>{i + 1}</td>
                                  <td>{dash(String(row?.confirmationNumber || "").trim())}</td>
                                  <td>{dash(String(row?.containerNumber || "").trim())}</td>
                                  <td>{dash(String(row?.blNumber || "").trim())}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* ───────────────── Ocean: Containers ───────────────── */}
                {activeTab === "containers" && isOcean && (
                  <div className="bdm-section">
                    <div className="bdm-section-label">Container Details</div>
                    {containerLineItems.length === 0 ? (
                      <div className="bdm-empty">
                        No container details available for this booking.
                      </div>
                    ) : (
                      <div className="bdm-charges-wrap">
                        <table className="bdm-dim-table">
                          <thead>
                            <tr>
                              <th>Container Type</th>
                              <th>Quantity</th>
                              <th>Weight</th>
                            </tr>
                          </thead>
                          <tbody>
                            {containerLineItems.map((item, i) => (
                              <tr key={i}>
                                <td>{dash(item?.type)}</td>
                                <td>{dash(item?.quantity)}</td>
                                <td>{dash(containerWeightLabel(item))}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {/* ───────────────── Charges ───────────────── */}
                {activeTab === "charges" && (
                  <div className="bdm-section">
                    <div className="bdm-section-label">Charge Breakdown</div>

                    {isOcean ? (
                      oceanGroupKeys.length === 0 ? (
                        <div className="bdm-empty">
                          No charge breakdown available for this booking.
                        </div>
                      ) : (
                        <>
                          <div className="bdm-charges-wrap">
                            <table className="bdm-charges-table">
                              <thead>
                                <tr>
                                  <th>Description</th>
                                  <th>Container</th>
                                  <th>Amount</th>
                                </tr>
                              </thead>
                              <tbody>
                                {oceanGroupKeys.map((key) => [
                                  <tr className="bdm-charge-group" key={`g-${key}`}>
                                    <td colSpan={2}>{GROUP_LABEL[key] || key}</td>
                                    <td>{usd(oceanGroupTotal(key))}</td>
                                  </tr>,
                                  ...oceanChargeGroups[key].map((charge, i) => {
                                    const amt =
                                      safeNum(charge.amountUsd) ??
                                      safeNum(charge.amount);
                                    const currency =
                                      charge.amountUsd != null
                                        ? "USD"
                                        : charge.rateCurrency || "USD";
                                    return (
                                      <tr
                                        className="bdm-charge-sub"
                                        key={`${key}-${i}`}
                                      >
                                        <td>{charge.description || "—"}</td>
                                        <td>{dash(charge.containerType)}</td>
                                        <td>
                                          {amt == null
                                            ? "—"
                                            : currency === "USD"
                                            ? usd(amt)
                                            : `${currency} ${Number(amt).toFixed(2)}`}
                                        </td>
                                      </tr>
                                    );
                                  }),
                                ])}
                                {gstUsd > 0 && (
                                  <tr className="bdm-charge-group" key="taxable">
                                    <td colSpan={2}>Taxable Amount</td>
                                    <td>{usd(taxableUsd)}</td>
                                  </tr>
                                )}
                                {gstUsd > 0 && (
                                  <tr className="bdm-charge-group" key="gst">
                                    <td colSpan={2}>GST Amount</td>
                                    <td>{usd(gstUsd)}</td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                            <div className="bdm-charges-total">
                              <span className="bdm-charges-total-label">
                                Net Amount
                              </span>
                              <span className="bdm-charges-total-val">
                                {usd(totalUsd)}
                                {totalInr != null && (
                                  <span
                                    style={{
                                      fontSize: 12,
                                      fontWeight: 700,
                                      marginLeft: 6,
                                      color: "var(--ink-2)",
                                    }}
                                  >
                                    ({formatrates(Number(totalInr).toFixed(2))})
                                  </span>
                                )}
                              </span>
                            </div>
                          </div>
                          {gstUsd > 0 && (
                            <div className="bdm-charges-note">
                              <span>
                                <b>GST: </b>5% on Ocean Freight, 18% on all other
                                charges
                              </span>
                              {gstInr != null && (
                                <span>
                                  <b>GST (INR): </b>
                                  {formatrates(Number(gstInr).toFixed(2))}
                                </span>
                              )}
                            </div>
                          )}
                        </>
                      )
                    ) : airCharges.length === 0 ? (
                      <div className="bdm-empty">
                        No charge breakdown available for this booking.
                      </div>
                    ) : (
                      // One flat row per chargesBreakDown entry: its `name` as
                      // the description, `ratePerkilo` as the rate (a charge
                      // billed as a lump sum has none, so it reads "Flat") and
                      // `Totalamount` as the amount.
                      <div className="bdm-charges-wrap">
                        <table className="bdm-charges-table">
                          <thead>
                            <tr>
                              <th>Description</th>
                              <th>Rate</th>
                              <th>Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {airCharges.map((charge, i) => (
                              <tr key={i}>
                                <td>
                                  {getChargeDescription(charge) || "No description"}
                                </td>
                                <td>
                                  {charge?.ratePerkilo != null
                                    ? `${formatrates(charge.ratePerkilo)} / kg`
                                    : "Flat"}
                                </td>
                                <td>
                                  {formatrates(getChargeAmount(charge).toFixed(2))}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <div className="bdm-charges-total">
                          <span className="bdm-charges-total-label">
                            Total Charges
                          </span>
                          <span className="bdm-charges-total-val">
                            {totalInr != null
                              ? formatrates(Number(totalInr).toFixed(2))
                              : formatrates(
                                  airCharges
                                    .reduce((s, c) => s + getChargeAmount(c), 0)
                                    .toFixed(2)
                                )}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default CancelledBookingDetailsModal;
